// Updated Uploader Component that maintains dropSubmit=true when uploading more files
import React, { useEffect, useRef } from 'react';
import { useAuthentication } from './hooks/useAuthentication';
import { useNotifications } from './hooks/useNotifications';
import { useFileManagement } from './hooks/useFileManagement';
import { useFileUpload } from './hooks/useFileUpload';
import { getFileRejectionMessage } from './utils/helpers';
import { VIDEO_LIMIT, MAX_SIZE_BYTES } from './utils/constants';

// Import components
import DropzoneArea from './components/DropzoneArea';
import FileList from './components/FileList';
import ActionButtons from './components/ActionButtons';
import StatusBar from './components/StatusBar';
import CompletionStatus from './components/CompletionStatus';
import { NotificationsContainer } from '../Notification/Notification';

import './Uploader.css'; // Import CSS styles

/**
 * Accessible file uploader component
 * - Fixed to maintain dropSubmit=true when uploading more files
 * - Improved keyboard navigation
 * - Enhanced screen reader support
 * - Better focus management
 * - Clear error announcements
 */
const Uploader = () => {
  // Initialize notification system
  const { notifications, addNotification, removeNotification, throttledNotification } =
    useNotifications();

  // Initialize authentication
  const { token } = useAuthentication(addNotification);

  // Initialize file management
  const {
    files,
    setFiles,
    isComplete,
    setIsComplete,
    videoCount,
    imageCount,
    totalSize,
    fileCount,
    handleFilesDrop,
    removeFile,
    clearAllFiles,
  } = useFileManagement(addNotification, throttledNotification);

  // Initialize file upload
  const {
    uploadProgress,
    isUploading,
    uploadStatus,
    currentUploadingId,
    mediaFiles,
    fileListRef,
    fileItemRefs,
    handleUpload,
    retryUpload,
    resetUploadState,
  } = useFileUpload({
    files,
    addNotification,
    token,
    setIsComplete,
  });

  // Ref for screen reader announcements
  const srAnnouncementRef = useRef(null);

  // Ref for returning focus after actions
  const mainUploadButtonRef = useRef(null);

  // Auto-scroll effect and announce current file upload for screen readers
  useEffect(() => {
    if (currentUploadingId && fileItemRefs.current[currentUploadingId] && fileListRef.current) {
      const fileItemElement = fileItemRefs.current[currentUploadingId];
      const fileListElement = fileListRef.current;

      // Find the current file for announcement
      const currentFile = files.find(file => file.id === currentUploadingId);

      if (currentFile && srAnnouncementRef.current) {
        srAnnouncementRef.current.textContent = `Uploading ${currentFile.name}, ${uploadProgress[currentUploadingId]}% complete`;
      }

      // Smooth scroll to the file item
      fileItemElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [currentUploadingId, uploadProgress, files]);

  // Announce completion to screen readers
  useEffect(() => {
    if (isComplete && srAnnouncementRef.current) {
      srAnnouncementRef.current.textContent = 'All files successfully uploaded';
    }
  }, [isComplete]);

  // Clean up effect
  useEffect(() => {
    return () => {
      // Clean up the preview URLs when the component unmounts
      files.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [files]);

  // Handle rejected files with accessible announcements
  const handleRejectedFiles = rejectedFiles => {
    const errors = rejectedFiles.map(file => getFileRejectionMessage(file));
    const errorMessage = errors.join(' ');

    addNotification('error', errorMessage);

    // Announce to screen readers
    if (srAnnouncementRef.current) {
      srAnnouncementRef.current.textContent = `File upload error: ${errorMessage}`;
    }
  };

  // Handle upload more button click with focus management
  // FIXED: Keeps dropSubmit=true when uploading more files
  const handleUploadMore = () => {
    // Reset UI state to allow more uploads
    setIsComplete(false);
    resetUploadState();
    setFiles([]);

    // IMPORTANT: Keep the dropSubmit hidden field value as "true"
    // This ensures the form remembers files have been uploaded
    const dropSubmitField = document.getElementById('dropSubmit');
    if (dropSubmitField) {
      dropSubmitField.value = 'true';
    }

    // Return focus to main upload area
    if (mainUploadButtonRef.current) {
      setTimeout(() => {
        mainUploadButtonRef.current.focus();
      }, 0);
    }
  };

  return (
    <div
      className="container-fluid uploader-container py-4"
      role="region"
      aria-label="File Upload Interface">
      {/* Screen reader only announcement area */}
      <div ref={srAnnouncementRef} className="sr-only" aria-live="polite" role="status"></div>

      <div className="d-flex flex-column gap-4">
        {/* Conditional layout based on file presence */}
        {files.length === 0 ? (
          // Centered dropzone when no files are present
          <DropzoneArea
            hasFiles={false}
            isUploading={isUploading}
            isComplete={isComplete}
            onFilesDrop={handleFilesDrop}
            onDropRejected={handleRejectedFiles}
            videoLimit={VIDEO_LIMIT}
            maxSizeBytes={MAX_SIZE_BYTES}
            mainButtonRef={mainUploadButtonRef}
          />
        ) : (
          // Two-column layout when files are present
          <div className="row file-management-container">
            <div className="col-12 mb-3">
              <StatusBar
                imageCount={imageCount}
                videoCount={videoCount}
                totalSize={totalSize}
                videoLimit={VIDEO_LIMIT}
              />
            </div>

            {/* Dropzone column */}
            <div className="col-md-6 mb-4 mb-md-0">
              <DropzoneArea
                hasFiles={true}
                isUploading={isUploading}
                isComplete={isComplete}
                onFilesDrop={handleFilesDrop}
                onDropRejected={handleRejectedFiles}
                videoLimit={VIDEO_LIMIT}
                maxSizeBytes={MAX_SIZE_BYTES}
                mainButtonRef={mainUploadButtonRef}
              />
            </div>

            {/* File list and controls column */}
            <div className="col-md-6">
              {/* Action buttons */}
              {isComplete ? (
                <CompletionStatus onUploadMore={handleUploadMore} />
              ) : (
                <ActionButtons
                  isUploading={isUploading}
                  isComplete={isComplete}
                  onUpload={handleUpload}
                  onClear={clearAllFiles}
                  fileCount={fileCount}
                  videoCount={videoCount}
                  videoLimit={VIDEO_LIMIT}
                />
              )}

              {/* File preview list with accessible heading */}
              <div role="region" aria-label="Selected files">
                <h3 className="visually-hidden">Selected files</h3>
                <FileList
                  files={files}
                  uploadProgress={uploadProgress}
                  currentUploadingId={currentUploadingId}
                  isUploading={isUploading}
                  isComplete={isComplete}
                  onRemoveFile={removeFile}
                  onRetryUpload={retryUpload}
                  fileListRef={fileListRef}
                  fileItemRefs={fileItemRefs}
                />
              </div>
            </div>
          </div>
        )}

        {/* Notifications container with improved accessibility */}
        <NotificationsContainer
          notifications={notifications}
          removeNotification={removeNotification}
        />

        {/* Hidden form fields */}
        <input type="hidden" name="dropSubmit" id="dropSubmit" value={isComplete.toString()} />
        <input
          type="hidden"
          name="imageArray"
          id="imageArray"
          value={mediaFiles.images.join(',')}
        />
        <input
          type="hidden"
          name="videoArray"
          id="videoArray"
          value={mediaFiles.videos.join(',')}
        />
      </div>
    </div>
  );
};

// Export both as default and named export to support both import styles
export { Uploader };
export default Uploader;
